/** LA VOIX — le pro parle, Servae prépare, le pro valide.
 *  Rien n'est appliqué sans confirmation : une commande vocale mal comprise ne doit
 *  jamais casser un agenda. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, Check, Loader2, Mic, Send, Square, Sparkles, Trash2,
} from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, EnTete, Micro, Vide } from "../components/Bits";

const LONG = { timeout: 120000 };

function useMicro(surFichier) {
  const [enCours, setEnCours] = useState(false);
  const [secondes, setSecondes] = useState(0);
  const ref = useRef(null);

  const arreter = useCallback(() => {
    ref.current?.recorder?.stop();
  }, []);

  const demarrer = useCallback(async () => {
    try {
      const flux = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(flux);
      const morceaux = [];
      recorder.ondataavailable = (e) => e.data.size && morceaux.push(e.data);
      recorder.onstop = () => {
        flux.getTracks().forEach((t) => t.stop());
        clearInterval(ref.current?.timer);
        setEnCours(false);
        setSecondes(0);
        const blob = new Blob(morceaux, { type: recorder.mimeType || "audio/webm" });
        if (blob.size > 800) surFichier(blob);
        else toast.error("Enregistrement trop court.");
      };
      recorder.start();
      const timer = setInterval(() => {
        setSecondes((s) => {
          if (s >= 59) recorder.stop();
          return s + 1;
        });
      }, 1000);
      ref.current = { recorder, timer };
      setEnCours(true);
    } catch {
      toast.error("Micro inaccessible. Autorise le micro dans ton navigateur.");
    }
  }, [surFichier]);

  return { enCours, secondes, demarrer, arreter };
}

function Dicter({ enCours, secondes, demarrer, arreter, occupe, invite, testid }) {
  return (
    <div
      className="flex items-center gap-4 p-4"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
      }}
    >
      <button
        onClick={enCours ? arreter : demarrer}
        disabled={occupe}
        data-testid={testid}
        className="relative inline-flex h-16 w-16 shrink-0 items-center justify-center cible-tactile"
        style={{
          borderRadius: 999,
          background: enCours ? "var(--danger)" : "var(--accent)",
          color: "#fff",
          opacity: occupe ? 0.5 : 1,
          transition: "background-color .2s ease, transform .2s ease",
          transform: enCours ? "scale(1.04)" : "none",
          boxShadow: enCours ? "0 0 0 8px rgba(200,60,60,.12)" : "var(--shadow-sm)",
        }}
      >
        {occupe ? (
          <Loader2 size={24} className="animate-spin" />
        ) : enCours ? (
          <Square size={20} />
        ) : (
          <Mic size={24} strokeWidth={1.8} />
        )}
      </button>
      <div className="min-w-0">
        <p className="text-[15px] font-semibold" style={{ color: "var(--text)" }}>
          {occupe ? "Je t'écoute et je réfléchis…" : enCours ? "J'enregistre. Parle." : invite}
        </p>
        <p className="text-[13px] num" style={{ color: "var(--text-subtle)" }}>
          {enCours ? `${secondes}s — appuie pour terminer` : "1 minute maximum"}
        </p>
      </div>
    </div>
  );
}

function Champ({ valeur, surChangement, surEnvoi, occupe, placeholder, testid }) {
  return (
    <div className="flex gap-2 mt-3">
      <input
        value={valeur}
        onChange={(e) => surChangement(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && surEnvoi()}
        placeholder={placeholder}
        data-testid={testid}
        className="flex-1 h-11 px-3.5 text-[15px] outline-none focus:border-[var(--accent)]"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
        }}
      />
      <Btn icone={Send} onClick={surEnvoi} disabled={occupe || !valeur.trim()} data-testid={`${testid}-envoyer`}>
        Envoyer
      </Btn>
    </div>
  );
}

function Transcription({ texte }) {
  if (!texte) return null;
  return (
    <p
      className="mt-4 p-3.5 text-[14.5px] italic"
      data-testid="voix-transcription"
      style={{
        background: "var(--accent-soft)",
        borderRadius: "var(--radius-md)",
        color: "var(--text)",
      }}
    >
      « {texte} »
    </p>
  );
}

/* ------------------------------------------------------------------ commande */
function Commande() {
  const [texte, setTexte] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [res, setRes] = useState(null);
  const [retenues, setRetenues] = useState({});

  const envoyer = useCallback(async (fd) => {
    setOccupe(true);
    try {
      const { data } = await api.post("/voix/commande", fd, LONG);
      setRes(data);
      setRetenues(Object.fromEntries((data.actions || []).map((_, i) => [i, true])));
      if (!data.actions?.length) toast.message(data.commentaire || "Rien à faire.");
    } catch (e) {
      toast.error(messageErreur(e, "Je n'ai pas pu traiter ta demande."));
    } finally {
      setOccupe(false);
    }
  }, []);

  const parAudio = useCallback(
    (blob) => {
      const fd = new FormData();
      fd.append("audio", blob, "voix.webm");
      envoyer(fd);
    },
    [envoyer]
  );

  const micro = useMicro(parAudio);

  const parTexte = () => {
    if (!texte.trim()) return;
    const fd = new FormData();
    fd.append("texte", texte.trim());
    envoyer(fd);
    setTexte("");
  };

  const appliquer = async () => {
    const actions = (res.actions || []).filter((_, i) => retenues[i]);
    if (!actions.length) return;
    setOccupe(true);
    try {
      const { data } = await api.post("/voix/appliquer", { actions }, LONG);
      const ko = data.resultats.filter((r) => !r.ok);
      if (data.appliquees) toast.success(`${data.appliquees} action${data.appliquees > 1 ? "s" : ""} appliquée${data.appliquees > 1 ? "s" : ""}.`);
      ko.forEach((r) => toast.error(`${r.libelle} : ${r.message}`));
      setRes(null);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div data-testid="voix-commande">
      <Dicter {...micro} occupe={occupe} testid="voix-micro-commande"
        invite="Dis-moi ce que tu veux changer dans ton agenda." />
      <Champ
        valeur={texte}
        surChangement={setTexte}
        surEnvoi={parTexte}
        occupe={occupe}
        placeholder="ou écris : déplace Léa à jeudi 14h"
        testid="voix-texte-commande"
      />

      {!res && (
        <div className="mt-5 space-y-1.5">
          <Micro className="mb-2 block">Ce que tu peux dire</Micro>
          {[
            "Déplace Léa à jeudi 14 h.",
            "Bloque vendredi après-midi, j'ai un rendez-vous médical.",
            "Sarah m'a posé un lapin.",
            "Chloé a payé 55 en espèces.",
            "Note pour Inès : gel rose, forme amande, sensible sur l'ongle du pouce.",
          ].map((e) => (
            <p key={e} className="text-[14px]" style={{ color: "var(--text-muted)" }}>
              « {e} »
            </p>
          ))}
        </div>
      )}

      <Transcription texte={res?.transcription} />

      {res?.actions?.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="voix-actions">
          {res.actions.map((a, i) => (
            <button
              key={i}
              onClick={() => setRetenues((r) => ({ ...r, [i]: !r[i] }))}
              data-testid={`voix-action-${i}`}
              className="w-full flex items-start gap-3 text-left p-3.5"
              style={{
                background: "var(--surface)",
                border: `1px solid ${retenues[i] ? "var(--accent)" : "var(--border)"}`,
                borderRadius: "var(--radius-md)",
              }}
            >
              <span
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center mt-0.5"
                style={{
                  borderRadius: 8,
                  background: retenues[i] ? "var(--accent)" : "transparent",
                  border: `1px solid ${retenues[i] ? "var(--accent)" : "var(--border)"}`,
                  color: "#fff",
                }}
              >
                {retenues[i] && <Check size={14} strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className="block text-[15px] font-semibold">{a.libelle}</span>
                <span className="block text-[13.5px]" style={{ color: "var(--text-muted)" }}>
                  {a.detail}
                </span>
                {a.avertissement && (
                  <span
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold"
                    style={{ color: "var(--danger)" }}
                  >
                    <AlertTriangle size={13} /> {a.avertissement}
                  </span>
                )}
              </span>
            </button>
          ))}
          <div className="flex gap-2 pt-1">
            <Btn icone={Check} onClick={appliquer} disabled={occupe} data-testid="voix-appliquer">
              Appliquer
            </Btn>
            <Btn variante="nu" icone={Trash2} onClick={() => setRes(null)} data-testid="voix-annuler">
              Laisser tomber
            </Btn>
          </div>
        </div>
      )}

      {res && !res.actions?.length && res.commentaire && (
        <p className="mt-3 text-[14px]" style={{ color: "var(--text-muted)" }}>
          {res.commentaire}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- journal */
function Journal() {
  const [etat, setEtat] = useState(null);
  const [texte, setTexte] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    api.get("/voix/etat").then(({ data }) => setEtat(data)).catch(() => setEtat({ clients_du_jour: [] }));
  }, []);

  const envoyer = useCallback(async (fd) => {
    setOccupe(true);
    try {
      const { data } = await api.post("/voix/journal", fd, LONG);
      setRes(data);
      if (!data.notes?.length) toast.message("Je n'ai pas réussi à rattacher ce que tu as dit à un client.");
    } catch (e) {
      toast.error(messageErreur(e, "Impossible de ranger ce compte-rendu."));
    } finally {
      setOccupe(false);
    }
  }, []);

  const parAudio = useCallback(
    (blob) => {
      const fd = new FormData();
      fd.append("audio", blob, "journal.webm");
      envoyer(fd);
    },
    [envoyer]
  );

  const micro = useMicro(parAudio);

  const parTexte = () => {
    if (!texte.trim()) return;
    const fd = new FormData();
    fd.append("texte", texte.trim());
    envoyer(fd);
    setTexte("");
  };

  const enregistrer = async () => {
    setOccupe(true);
    try {
      const { data } = await api.post("/voix/journal/appliquer", {
        notes: res.notes.map((n) => ({
          client_id: n.client_id,
          note: n.note,
          preferences: n.preferences || null,
        })),
      });
      toast.success(`${data.enregistrees} fiche${data.enregistrees > 1 ? "s" : ""} mise${data.enregistrees > 1 ? "s" : ""} à jour.`);
      setRes(null);
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  };

  if (etat && !etat.clients_du_jour.length) {
    return (
      <Vide
        icone={Sparkles}
        titre="Aucun client aujourd'hui"
        texte="Le journal du soir se remplit à partir des rendez-vous de la journée."
      />
    );
  }

  return (
    <div data-testid="voix-journal">
      <p className="text-[14px] mb-4" style={{ color: "var(--text-muted)" }}>
        {etat
          ? `${etat.clients_du_jour.length} client${etat.clients_du_jour.length > 1 ? "s" : ""} ${etat.jour_label}. Raconte ta journée, je range dans les fiches.`
          : "…"}
      </p>
      <Dicter {...micro} occupe={occupe} testid="voix-micro-journal"
        invite="Raconte ta journée, client par client." />
      <Champ
        valeur={texte}
        surChangement={setTexte}
        surEnvoi={parTexte}
        occupe={occupe}
        placeholder="ou écris ton compte-rendu"
        testid="voix-texte-journal"
      />

      {etat && !res && (
        <div className="mt-5">
          <Micro className="mb-2 block">Tes clientes du jour</Micro>
          {etat.clients_du_jour.map((c) => (
            <p key={c.id + c.heure} className="text-[14px]" style={{ color: "var(--text-muted)" }}>
              <span className="num">{c.heure}</span> · {c.nom} — {c.prestation}
            </p>
          ))}
        </div>
      )}

      <Transcription texte={res?.transcription} />

      {res?.notes?.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="voix-journal-notes">
          {res.notes.map((n, i) => (
            <div
              key={i}
              data-testid={`voix-journal-note-${i}`}
              className="p-3.5"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <p className="text-[15px] font-semibold">
                {n.nom}
                <span className="font-normal text-[13px]" style={{ color: "var(--text-subtle)" }}>
                  {" "}· {n.prestation}
                </span>
              </p>
              <textarea
                value={n.note}
                onChange={(e) =>
                  setRes((r) => ({
                    ...r,
                    notes: r.notes.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)),
                  }))
                }
                rows={3}
                className="w-full mt-2 p-2.5 text-[14.5px] outline-none focus:border-[var(--accent)]"
                style={{
                  background: "var(--surface-sunken)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  resize: "vertical",
                }}
              />
              {n.preferences && (
                <p className="text-[13px] mt-2" style={{ color: "var(--accent)" }}>
                  Préférences : {n.preferences}
                </p>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Btn icone={Check} onClick={enregistrer} disabled={occupe} data-testid="voix-journal-enregistrer">
              Enregistrer dans les fiches
            </Btn>
            <Btn variante="nu" onClick={() => setRes(null)} data-testid="voix-journal-annuler">
              Laisser tomber
            </Btn>
          </div>
        </div>
      )}

      {res?.general && (
        <p className="mt-3 text-[14px]" style={{ color: "var(--text-subtle)" }}>
          {res.general}
        </p>
      )}
    </div>
  );
}

export default function Voix() {
  const [onglet, setOnglet] = useState("commande");
  return (
    <div className="pb-8" data-testid="page-voix">
      <EnTete
        micro="La Voix"
        titre="Parle, Servae s'occupe du reste"
        sous="Les mains occupées, tu dis ce qu'il faut changer. Rien n'est appliqué sans ton feu vert."
      />

      <div
        className="flex p-1 gap-1 mt-6 w-fit"
        style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
      >
        {[
          { c: "commande", l: "Commande" },
          { c: "journal", l: "Journal du soir" },
        ].map((t) => {
          const on = onglet === t.c;
          return (
            <button
              key={t.c}
              onClick={() => setOnglet(t.c)}
              data-testid={`voix-onglet-${t.c}`}
              className="h-9 px-4 text-[13px] font-semibold cible-tactile"
              style={{
                borderRadius: "var(--radius-full)",
                background: on ? "var(--surface)" : "transparent",
                color: on ? "var(--text)" : "var(--text-subtle)",
                boxShadow: on ? "var(--shadow-sm)" : "none",
              }}
            >
              {t.l}
            </button>
          );
        })}
      </div>

      <div className="mt-6 max-w-[680px]">{onglet === "commande" ? <Commande /> : <Journal />}</div>
    </div>
  );
}
